import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '../common/icon';
import { platformImageUrl } from '@/utils/config';

const LOWER_FREE_BANNER_HIDDEN_KEY = 'lower_free_banner_hidden';

export function shouldShowLowerFreeBanner() {
  if (typeof window === 'undefined') return true;
  try {
    return localStorage.getItem(LOWER_FREE_BANNER_HIDDEN_KEY) !== '1';
  } catch {
    return true;
  }
}

export default function LowerFreeBanner({
  className,
  onClose
}: {
  className?: string;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const [isShow, setIsShow] = useState(shouldShowLowerFreeBanner);

  const handleClose = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      localStorage.setItem(LOWER_FREE_BANNER_HIDDEN_KEY, '1');
    } catch {
      // ignore storage errors and still hide for current session
    }
    setIsShow(false);
    onClose?.();
  };

  if (!isShow) return null;

  return (
    <div className={`px-4 md:px-0 ${className}`}>
      <div className="relative md:px-4 md:py-[11px] rounded-[16px] bg-[radial-gradient(100%_100%_at_50%_0%,_rgba(255,_212,_212,_0.9)_0%,_#FFE7CE_100%)] items-center gap-2 md:mb-8 overflow-hidden hidden md:flex">
        <img
          src={`${platformImageUrl}lower-free-coin.png`}
          alt=""
          className="w-[90px] shrink-0"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1 relative z-[2]">
          <div className="px-2 py-1 bg-[linear-gradient(180deg,#FF5903_0%,#F15200_100%)] rounded-xl flex justify-center items-center gap-2 overflow-hidden w-fit border border-solid [border-image-source:linear-gradient(180deg,rgba(247,85,1,0.2)_0%,rgba(255,167,80,0.2)_100%),radial-gradient(63.87%_50%_at_52.08%_100%,#FFFFFF_0%,rgba(255,255,255,0)_100%)] [border-image-slice:1] shadow-[inset_0px_1px_18px_2px_#F0ABAB]">
            <div className="text-center justify-center text-white text-xs font-normal font-['Inter'] leading-4">
              {t('lowerFreeBanner.desktopBadge', {
                defaultValue: 'LIMITED TIME · LOWER FEES'
              })}
            </div>
          </div>
          <p className="min-w-0 text-[#1C1917] text-base font-medium font-['ES_Build_TRIAL','Inter',sans-serif] leading-7 md:text-lg md:leading-8 2xl:text-xl 2xl:leading-9 xl:whitespace-nowrap">
            {t('lowerFreeBanner.feeReduction', {
              defaultValue: 'Service fee is slashes to'
            })}{' '}
            <span className="font-normal line-through">
              {t('lowerFreeBanner.originalFee', { defaultValue: '15%' })}
            </span>{' '}
            <span className="bg-gradient-to-r from-[#FB6011] to-[#FF922B] bg-clip-text text-transparent text-2xl font-bold 2xl:text-3xl">
              {t('lowerFreeBanner.newFee', { defaultValue: '5%' })}
            </span>
            {t('lowerFreeBanner.desktopSuffix', {
              defaultValue:
                '! Deliver on time and we drop a 5% bonus in your pocket, so you keep 100%.'
            })}
          </p>
        </div>
        <img
          src={`${platformImageUrl}lower-free-a.png`}
          alt=""
          className="absolute right-[188px] top-0 hidden w-[126px] min-[1920px]:block"
        />
        <img
          src={`${platformImageUrl}lower-free-shake.png`}
          alt=""
          className="absolute right-[56px] top-0 hidden w-[72px] md:block"
        />
        <div
          className="absolute right-2 top-2 flex h-[36px] w-[36px] cursor-pointer items-center justify-center z-[3]"
          onClick={handleClose}
        >
          <CloseIcon className="w-4 h-4 text-[#525252]" />
        </div>
      </div>
      <div className="md:hidden pt-4 pb-[22px] flex flex-col gap-2 items-center rounded-[16px] bg-[radial-gradient(100%_100%_at_50%_0%,_rgba(255,_212,_212,_0.9)_0%,_#FFE7CE_100%)] mb-6 relative overflow-hidden">
        <div className="px-2 py-1 bg-[linear-gradient(180deg,#FF5903_0%,#F15200_100%)] rounded-xl flex justify-center items-center gap-2 overflow-hidden w-fit border border-solid [border-image-source:linear-gradient(180deg,rgba(247,85,1,0.2)_0%,rgba(255,167,80,0.2)_100%),radial-gradient(63.87%_50%_at_52.08%_100%,#FFFFFF_0%,rgba(255,255,255,0)_100%)] [border-image-slice:1] shadow-[inset_0px_1px_18px_2px_#F0ABAB]">
          <div className="text-center justify-center text-white text-xs font-normal font-['Inter'] leading-4">
            {t('lowerFreeBanner.mobileBadge', {
              defaultValue: 'LIMITED-TIME SUBSIDY'
            })}
          </div>
        </div>
        <div className="w-[286px] text-center justify-center">
          <span className="text-Colors-Text-text-primary-(900) text-xl font-medium font-['ES_Build_TRIAL'] leading-7">
            {t('lowerFreeBanner.feeReduction', {
              defaultValue: 'Service fee is slashes to'
            })}{' '}
            <br />
          </span>
          <span className="font-normal line-through">
            {t('lowerFreeBanner.originalFee', { defaultValue: '15%' })}
          </span>{' '}
          <span className="bg-gradient-to-r from-[#FB6011] to-[#FF922B] bg-clip-text text-transparent text-2xl font-bold 2xl:text-3xl">
            {t('lowerFreeBanner.newFee', { defaultValue: '5%' })}
          </span>
          <span className="text-Colors-Text-text-primary-(900) text-xl font-medium font-['ES_Build_TRIAL'] leading-7">
            {t('lowerFreeBanner.mobileSuffix', { defaultValue: ' !' })}
            <br />
            {t('lowerFreeBanner.mobileDescription', {
              defaultValue:
                'Deliver on time and we drop a 5% bonus in your pocket, so you keep 100%.'
            })}
          </span>
        </div>
        <div
          className="absolute right-2 top-2 flex h-[36px] w-[36px] cursor-pointer items-center justify-center z-[3]"
          onClick={handleClose}
        >
          <CloseIcon className="w-4 h-4 text-[#525252]" />
        </div>
        <img
          src={`${platformImageUrl}lower-free-shake.png`}
          alt=""
          className="absolute right-[-46px] bottom-0 w-[72px]"
        />
        <img
          src={`${platformImageUrl}lower-free-coin.png`}
          alt=""
          className="w-[80px] shrink-0 absolute left-[-40px] bottom-[0px]"
        />
      </div>
    </div>
  );
}
